import { queryOptions, useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { aciIntegrationsService } from 'v2/shared/services/aci-integrations-service'
import { isEmbedMode } from 'v2/shared/utils/embed-session'
import { externalAppsQueryKeys } from 'v2/shared/utils/query-key-management'
import type { RExternalApp } from 'v2/types/api/settings'
import { errorToast } from 'v2/utils/toasts'

export const createIntegrationConnectionsQueryOptions = (isActive?: boolean) =>
  queryOptions({
    queryKey: externalAppsQueryKeys().externalOrgAppsV3(isActive),
    // The integrations service is not on the embed allow-list.
    enabled: !isEmbedMode(),
    queryFn: async () => {
      const response = await aciIntegrationsService.getConnections()
      return isActive
        ? response.data.filter((connection) => connection.state === 'ACTIVE')
        : response.data
    },
  })

// The catalog is not on the embed allow-list either.
export const useExternalAppsQuery = () =>
  useQuery<RExternalApp>({
    queryKey: externalAppsQueryKeys().externalApps,
    queryFn: async () => (await aciIntegrationsService.getCatalog()).data,
    enabled: !isEmbedMode(),
  })

export const useExternalAppsV2Query = () =>
  useQuery<RExternalApp>({
    queryKey: externalAppsQueryKeys().externalAppsV2,
    queryFn: async () => (await aciIntegrationsService.getCatalog()).data,
    enabled: !isEmbedMode(),
  })

export const useExternalOrgAppsQuery = (isActive?: boolean, enabled = true) => {
  const options = createIntegrationConnectionsQueryOptions(isActive)
  return useQuery({ ...options, enabled: options.enabled && enabled })
}

export const useOAuthProvidersQuery = (enabled = true) => {
  const query = useQuery({
    queryKey: externalAppsQueryKeys().oauthProviders,
    queryFn: async () => (await aciIntegrationsService.getOAuthProviders()).data ?? [],
    enabled,
  })

  useEffect(() => {
    if (query.isError) {
      errorToast({ title: 'Error', description: 'Failed to load OAuth providers' })
    }
  }, [query.isError])

  return query
}

export const useCustomFieldsQuery = (
  appId: string,
  isCustomFieldsEnabled: boolean,
  entities: string[],
  enabled: boolean,
) =>
  useQuery({
    queryKey: externalAppsQueryKeys().customFields(appId),
    enabled: isCustomFieldsEnabled && !!appId && enabled && entities.length > 0,
    queryFn: async () => {
      const response = await aciIntegrationsService.getCustomFields(appId, entities)
      return response.data?.fields_by_entity || {}
    },
  })

export const useAccessRecordsQuery = (orgAppUuid: string, enabled = true) =>
  useQuery({
    queryKey: externalAppsQueryKeys().accessRecords(orgAppUuid),
    queryFn: async () => (await aciIntegrationsService.getAccessRecords(orgAppUuid)).data,
    enabled: Boolean(orgAppUuid && enabled),
  })
